import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { BRANDS_API, normalizeBrand } from "../../shared/catalogMastersApi";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  parentId: "",
  name: "",
  slug: "",
  logo: "",
  banner: "",
  description: "",
  metaTitile: "",
  metaDescription: ""
});

export function BrandsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [parentsOptions, setParentsOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const res = await apiGet(BRANDS_API);
    if (res.ok) {
      const list = Array.isArray(res.data) ? res.data.map(normalizeBrand).filter(Boolean) : [];
      setParentsOptions(list);
    } else setError(res.error ?? "Failed to load brands");
    setLoading(false);
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`${BRANDS_API}/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load brand");
        return;
      }
      const x = normalizeBrand(res.data);
      if (!x) return;
      setEditingId(x.id);
      setForm({
        parentId: x.parentId ?? "",
        name: x.name ?? "",
        slug: x.slug ?? "",
        logo: x.logo ?? "",
        banner: x.banner ?? "",
        description: x.description ?? "",
        metaTitile: x.metaTitile ?? "",
        metaDescription: x.metaDescription ?? ""
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    const res = await apiPost(BRANDS_API, {
      id: editingId ?? 0,
      parentId: form.parentId ? Number(form.parentId) : null,
      name: form.name,
      slug: form.slug || null,
      logo: form.logo || null,
      banner: form.banner || null,
      description: form.description || null,
      metaTitile: form.metaTitile || null,
      metaDescription: form.metaDescription || null
    });
    if (!res.ok) setError(res.error ?? "Failed to save brand");
    else {
      await loadLookups();
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    await loadLookups();
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`${BRANDS_API}/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load brand");
        return;
      }
      const x = normalizeBrand(res.data);
      if (!x) return;
      setEditingId(x.id);
      setForm({
        parentId: x.parentId ?? "",
        name: x.name ?? "",
        slug: x.slug ?? "",
        logo: x.logo ?? "",
        banner: x.banner ?? "",
        description: x.description ?? "",
        metaTitile: x.metaTitile ?? "",
        metaDescription: x.metaDescription ?? ""
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Brand" : "Add Brand"}</h2>
        </div>
        <div className="card-header-actions">
          <button className="icon-button icon-button--toolbar" type="submit" form="brands-form" disabled={saving} title="Save" aria-label="Save brand">
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/brands-list" className="icon-button icon-button--toolbar" title="Brand list" aria-label="Open brand list">
            <IconList />
          </Link>
        </div>
      </div>
      <div className="card-body">
        <form id="brands-form" onSubmit={onSubmit} className="form form-fields-bottomline">
          <div className="form-row form-row--3">
            <label className="field">
              <span>Name</span>
              <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </label>
            <label className="field">
              <span>Slug</span>
              <input type="text" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))} />
            </label>
            <label className="field">
              <span>Description</span>
              <textarea rows={1} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            </label>
          </div>
          <div className="form-row form-row--4">
            <label className="field">
              <span>Parent Brand</span>
              <select value={form.parentId} onChange={(e) => setForm((p) => ({ ...p, parentId: e.target.value ? Number(e.target.value) : "" }))}>
                <option value="">None</option>
                {parentsOptions
                  .filter((x) => x.id !== editingId)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="field">
              <span>Meta Title</span>
              <input type="text" value={form.metaTitile} onChange={(e) => setForm((p) => ({ ...p, metaTitile: e.target.value }))} />
            </label>
            <label className="field">
              <span>Meta Description</span>
              <input type="text" value={form.metaDescription} onChange={(e) => setForm((p) => ({ ...p, metaDescription: e.target.value }))} />
            </label>
          </div>
          <div className="form-row form-row--4">
            <ImageUploadField label="Logo" value={form.logo} onChange={(logo) => setForm((p) => ({ ...p, logo }))} />
            <ImageUploadField label="Banner" value={form.banner} onChange={(banner) => setForm((p) => ({ ...p, banner }))} />
          </div>
          {error && <div className="error-banner">{error}</div>}
        </form>
      </div>
    </div>
  );
}
